import React, { useState, useEffect, useCallback, useMemo } from 'react';

const BINGO_COLORS = {
  B: '#3B82F6',
  I: '#EF4444',
  N: '#F59E0B',
  G: '#EC4899',
  O: '#8B5CF6',
};

const BINGO_LETTERS = ['B', 'I', 'N', 'G', 'O'];

const BINGO_RANGES = {
  B: Array.from({ length: 15 }, (_, i) => i + 1),
  I: Array.from({ length: 15 }, (_, i) => i + 16),
  N: Array.from({ length: 15 }, (_, i) => i + 31),
  G: Array.from({ length: 15 }, (_, i) => i + 46),
  O: Array.from({ length: 15 }, (_, i) => i + 61),
};

const columnsToGrid = (numbers) => {
  const cols = [numbers.B, numbers.I, numbers.N, numbers.G, numbers.O];
  const grid = [];
  for (let row = 0; row < 5; row++) {
    const rowData = [];
    for (let col = 0; col < 5; col++) {
      rowData.push(cols[col][row]);
    }
    grid.push(rowData);
  }
  return grid;
};

const BingoCardsGame = ({ cardNumbers, onBack }) => {
  const [cards, setCards] = useState([]);
  const [markedNumbers, setMarkedNumbers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showBoard, setShowBoard] = useState(true);

  useEffect(() => {
    const loadCards = async () => {
      setLoading(true);
      setError('');

      if (cardNumbers.length === 0) {
        setError('No cards selected');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${process.env.PUBLIC_URL}/phoenix.cards.json`);
        if (!res.ok) throw new Error('Failed to load cards');
        const allCards = await res.json();

        const results = [];
        for (const num of cardNumbers) {
          const card = allCards.find(c => c.cardNumber == num);
          if (!card) {
            setError(`Card #${num} not found`);
            setLoading(false);
            return;
          }
          results.push({
            cardNumber: card.cardNumber,
            grid: columnsToGrid(card.numbers),
          });
        }

        setCards(results);
      } catch (err) {
        setError('Failed to load cards. Please check your connection and try again.');
      }
      setLoading(false);
    };

    loadCards();
  }, [cardNumbers]);

  const toggleMark = useCallback((number) => {
    if (number === 'FREE' || number === 0 || number === null) return;
    setMarkedNumbers(prev => {
      const next = new Set(prev);
      if (next.has(number)) {
        next.delete(number);
      } else {
        next.add(number);
      }
      return next;
    });
  }, []);

  const handleReset = useCallback(() => {
    setMarkedNumbers(new Set());
  }, []);

  const layoutClass = useMemo(() => {
    const count = cards.length;
    if (count <= 12) return `bc-game-layout-${count}`;
    return 'bc-game-layout-12';
  }, [cards.length]);

  if (loading) {
    return (
      <div className="bc-game">
        <div className="bc-game-loading">
          <span className="bc-spinner large" />
          <p>Loading your cards...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bc-game">
        <div className="bc-game-error">
          <p>{error}</p>
          <button className="bc-entry-btn primary" style={{ maxWidth: 200 }} onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bc-game">
      <div className="bc-game-header">
        <button className="bc-back-btn" onClick={onBack}>&#8592;</button>
        <button
          className={`bc-board-toggle ${showBoard ? 'active' : ''}`}
          onClick={() => setShowBoard(prev => !prev)}
        >
          {showBoard ? 'Hide Board' : 'Board'}
        </button>
        <h2 className="bc-game-title">Phoenix Bingo</h2>
        <button className="bc-reset-btn" onClick={handleReset}>Reset</button>
      </div>

      {showBoard && (
        <div className="bc-caller-board">
          {BINGO_LETTERS.map((letter) => (
            <div key={letter} className="bc-caller-row">
              <div className="bc-caller-letter" style={{ background: BINGO_COLORS[letter] }}>
                {letter}
              </div>
              <div className="bc-caller-numbers">
                {BINGO_RANGES[letter].map((num) => (
                  <button
                    key={num}
                    className={`bc-caller-num ${markedNumbers.has(num) ? 'called' : ''}`}
                    style={markedNumbers.has(num) ? { background: BINGO_COLORS[letter] } : undefined}
                    onClick={() => toggleMark(num)}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={`bc-game-cards ${layoutClass}`}>
        {cards.map((card) => (
          <div key={card.cardNumber} className="bc-bingo-card">
            <div className="bc-bingo-card-number">Card {card.cardNumber}</div>
            <div className="bc-bingo-header">
              {BINGO_LETTERS.map((letter) => (
                <div
                  key={letter}
                  className="bc-bingo-header-cell"
                  style={{ background: BINGO_COLORS[letter] }}
                >
                  {letter}
                </div>
              ))}
            </div>
            <div className="bc-bingo-grid">
              {card.grid.map((row, rowIdx) =>
                row.map((cell, colIdx) => {
                  const isFree = cell === 'FREE';
                  const isMarked = isFree || markedNumbers.has(cell);
                  return (
                    <button
                      key={`${rowIdx}-${colIdx}`}
                      className={`bc-bingo-cell ${isMarked ? 'marked' : ''} ${isFree ? 'free' : ''}`}
                      onClick={() => !isFree && toggleMark(cell)}
                    >
                      <span className="bc-bingo-cell-number">{isFree ? 'FREE' : cell}</span>
                      {isFree && <span className="bc-bingo-cell-star">&#9733;</span>}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BingoCardsGame;
